// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * palette.ts — every colour that carries meaning, in one place.
 *
 * It lives here rather than inline in the renderer because of principle #22:
 * scrapwall drew its walls at 1.14:1 against the board — correctly placed,
 * correctly sized, and invisible — and the screenshot read as atmosphere. A
 * colour literal buried in a draw call cannot be pinned by a test; a constant
 * here can, and `tests/contrast.test.ts` holds every one of these to ≥3:1
 * against every surface it is actually painted on.
 */

export const PALETTE = {
  /** The ground the whole colony is drawn on. */
  ground: '#12180f',
  /** A cell that is a legal target — soil turned over, one shade up from ground. */
  soil: '#243020',
  /** Panels, hand tray, HUD. */
  panel: '#1a2216',
  panelEdge: '#38492f',

  /** Player 1 — moss. */
  moss: '#9fe08a',
  mossDeep: '#4e8f3e',
  /**
   * Player 2 — amber. Separated from moss by LIGHTNESS as well as hue (1.93:1),
   * so the two sides stay distinguishable to a player who cannot separate the
   * hues at all.
   *
   * The first pass at this palette was `#7fc96a` / `#e8a33d`, which measured
   * **1.07:1** — the same shade of grey — while the plan next to it claimed a
   * "lightness *and* hue split". That is turntide's shipped bug, reproduced
   * here from scratch, and `tests/contrast.test.ts` caught it in the first run.
   * A hue difference is not a difference if hue is the channel a player is
   * missing.
   */
  amber: '#e5983f',
  amberDeep: '#a8701f',

  /** Glyph drawn ON a piece — must contrast against moss AND amber. */
  glyph: '#0d1109',
  /** The Heartroot's inner mark, on either player's colour. */
  heart: '#0d1109',

  /** A legal destination marker, on ground or on soil. */
  target: '#cfe3c4',
  /** The currently selected piece's outline. */
  select: '#ffffff',
  /** Nested halo — supported, short-ranged. */
  nested: '#8fd8ff',
  /**
   * Reaching flare — exposed, long-ranged.
   *
   * Magenta rather than the obvious warm orange: an orange flare measured
   * 1.02:1 against the nested halo (indistinguishable) AND collided with
   * Amber's own piece colour, so a selected Amber piece would have flared in
   * something very close to itself.
   */
  reaching: '#ff6fd8',
  /** A Heartroot's pressure collar as it fills. */
  pressure: '#ff7a5c',
  /** One cell from death. */
  pressureCrit: '#ff4d3d',
  /** A piece frozen by an enemy Warden. */
  pinned: '#b9a7d9',

  text: '#e9f1e3',
  textMuted: '#a2b39a',
} as const;

export type PaletteKey = keyof typeof PALETTE;

/** Relative luminance per WCAG 2.1. */
export function luminance(hex: string): number {
  const h = hex.replace('#', '');
  const [r, g, b] = [0, 2, 4].map((i) => {
    const v = parseInt(h.slice(i, i + 2), 16) / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrast(a: string, b: string): number {
  const [x, y] = [luminance(a), luminance(b)];
  return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05);
}

/** The two seat colours, indexed by seat. */
export const SEAT_COLOUR = [PALETTE.moss, PALETTE.amber] as const;
export const SEAT_DEEP = [PALETTE.mossDeep, PALETTE.amberDeep] as const;
export const SEAT_NAME = ['Moss', 'Amber'] as const;
